// FirewallRules.tsx
import { useState, useEffect } from "react";
import {
    RefreshCw, Shield, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowLeft, CheckCircle2, XCircle,
    Activity, Tag, Lock,
    ArrowRightLeft, Hash
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";

// ── Types ─────────────────────────────────────────────────────────────────────
interface AdditionalInfo {
    utm_status: string;
    dscp_forward: string;
    traffic_shaper: string;
    inspection_mode: string;
}

interface RuleData {
    name: string;
    action: string;
    status: string;
    enabled: boolean;
    rule_id: string;
    service: string[];
    comments: string;
    schedule: any;
    hit_count: number;
    log_start: boolean;
    av_profile: string;
    byte_count: number;
    ips_sensor: string;
    web_filter: string;
    app_control: string;
    log_traffic: string;
    nat_enabled: boolean;
    rule_number: number;
    source_zone: string[];
    source_address: string[];
    ssl_inspection: string;
    additional_info: AdditionalInfo;
    destination_zone: string[];
    destination_address: string[];
    hit_count_description: string;
}

interface FirewallRuleItem {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    name: string;
    status: string;
    data: any;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseData = (raw: any): RuleData | null => {
    if (typeof raw === "object" && raw !== null) {
        return raw as RuleData;
    }
    try { return JSON.parse(raw); } catch { return null; }
};

const fmtBytes = (bytes: number): string => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024, sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const fmtNumber = (n: number): string => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
};

const formatScheduleArray = (schedule: any): string[] => {
    if (!schedule) return [];
    if (typeof schedule === "string") return [schedule];
    if (Array.isArray(schedule)) {
        const raw = schedule.join("");
        if (raw.includes(",")) return raw.split(",").map((s) => s.trim().replaceAll("_", " "));
        return [raw];
    }
    return [String(schedule)];
};

// ── Shared Primitives ─────────────────────────────────────────────────────────
const StatusBadge = ({ status }: { status: string }) => (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border ${status === "enable"
        ? "bg-green-500/10 border-green-500/20 text-green-400"
        : "bg-red-500/10 border-red-500/20 text-red-400"
        }`}>
        {status === "enable" ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
        {status === "enable" ? "Enabled" : "Disabled"}
    </span>
);

const ActionBadge = ({ action }: { action: string }) => (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase ${action === "accept"
        ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
        : action === "deny"
            ? "bg-red-500/10 border-red-500/20 text-red-400"
            : "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
        }`}>
        {action}
    </span>
);

const Chips = ({ items, color = "bg-slate-100 dark:bg-gray-700/60 border-slate-200 dark:border-gray-600/40 text-slate-700 dark:text-gray-300" }: { items: string[]; color?: string }) => (
    <div className="flex flex-wrap gap-1">
        {items.map((item, i) => (
            <span key={i} className={`px-1.5 py-0.5 text-[8px] font-bold border rounded uppercase ${color}`}>
                {item}
            </span>
        ))}
    </div>
);


const renderDetailRow = (label: string, value: any, color: string = "text-slate-800 dark:text-slate-200") => (
    <div key={label} className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-gray-700/40 last:border-0 hover:bg-slate-50 dark:hover:bg-gray-700/20 px-1 -mx-1 rounded transition-colors">
        <span className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest">{label}</span>
        <span className={`text-[10px] font-black ${color} text-right max-w-[65%] truncate`} title={String(value ?? "")}>
            {value !== null && value !== undefined && value !== "" ? String(value) : "—"}
        </span>
    </div>
);

// ── Detail View ───────────────────────────────────────────────────────────────
function RuleDetailView({ item, onBack }: { item: FirewallRuleItem; onBack: () => void }) {
    const d = parseData(item.data);
    const schedules = d ? formatScheduleArray(d.schedule) : [];

    return (
        <div className="space-y-3 animate-fade-in">

            {/* Header */}
            <div className="flex items-center justify-between gap-3 bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-2.5 rounded-xl shadow-sm">
                <div className="flex items-center gap-2.5">
                    <button
                        onClick={onBack}
                        className="p-1.5 bg-red-500/10 border border-red-500/30 rounded-md text-red-600 dark:text-red-400 hover:text-white hover:bg-red-500/20 transition-all group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <div className="bg-red-500/10 p-1.5 rounded-md border border-red-500/20">
                        <Shield className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-none">{item.name}</h2>
                            {d && <StatusBadge status={d.status} />}
                            {d && <ActionBadge action={d.action} />}
                        </div>
                        {d?.comments && <p className="text-[10px] text-slate-500 dark:text-gray-400 mt-0.5">{d.comments}</p>}
                    </div>
                </div>
                {d && (
                    <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                            <p className="text-[9px] text-slate-500 dark:text-gray-200 uppercase tracking-widest">Hit Count</p>
                            <p className="text-sm font-black text-gray-900 dark:text-white">{fmtNumber(d.hit_count)}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[9px] text-slate-500 dark:text-gray-200 uppercase tracking-widest">Bytes</p>
                            <p className="text-sm font-black text-gray-900 dark:text-white">{fmtBytes(d.byte_count)}</p>
                        </div>
                    </div>
                )}
            </div>

            {!d ? (
                <p className="text-slate-500 dark:text-gray-200 text-xs p-4">No Details available.</p>
            ) : (
                <>
                    {/* Row 1: Traffic Flow */}
                    <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200 dark:border-gray-700">
                            <ArrowRightLeft className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                            <h4 className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Traffic Flow</h4>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div>
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-1.5">Source Zone</p>
                                <Chips items={d.source_zone} color="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-300" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-1.5">Source Address</p>
                                <Chips items={d.source_address} color="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-300" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-1.5">Destination Zone</p>
                                <Chips items={d.destination_zone} color="bg-orange-500/10 border-orange-500/20 text-orange-600 dark:text-orange-300" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-1.5">Destination Address</p>
                                <Chips items={d.destination_address} color="bg-orange-500/10 border-orange-500/20 text-orange-600 dark:text-orange-300" />
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Config + Security + Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                        {/* Config */}
                        <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-slate-200 dark:border-gray-700 pb-1.5">
                                <Tag className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                                <h4 className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase tracking-widest">Configuration</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("Rule #", d.rule_number)}
                                {renderDetailRow("Action", d.action.toUpperCase(), d.action === "accept" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}
                                {renderDetailRow("Status", d.status, d.status === "enable" ? "text-emerald-600 dark:text-green-400" : "text-red-600 dark:text-red-400")}
                                {renderDetailRow("NAT", d.nat_enabled ? "Enabled" : "Disabled", d.nat_enabled ? "text-emerald-600 dark:text-green-400" : "text-slate-400 dark:text-gray-200")}
                                {renderDetailRow("Log Traffic", d.log_traffic)}
                                {renderDetailRow("Log Start", d.log_start ? "Yes" : "No")}
                                {renderDetailRow("SSL Inspection", d.ssl_inspection)}
                                {renderDetailRow("Inspection Mode", d.additional_info?.inspection_mode)}
                                {renderDetailRow("UTM Status", d.additional_info?.utm_status, d.additional_info?.utm_status === "enable" ? "text-emerald-600 dark:text-green-400" : "text-slate-400 dark:text-gray-200")}
                            </div>
                        </div>

                        {/* Security Profiles */}
                        <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-slate-200 dark:border-gray-700 pb-1.5">
                                <Lock className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400" />
                                <h4 className="text-[10px] font-black text-yellow-600 dark:text-yellow-400 uppercase tracking-widest">Security Profiles</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("AV Profile", d.av_profile || null)}
                                {renderDetailRow("IPS Sensor", d.ips_sensor || null)}
                                {renderDetailRow("Web Filter", d.web_filter || null)}
                                {renderDetailRow("App Control", d.app_control || null)}
                                {renderDetailRow("Traffic Shaper", d.additional_info?.traffic_shaper || null)}
                                {renderDetailRow("DSCP Forward", d.additional_info?.dscp_forward)}
                            </div>
                            <div className="mt-3">
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-1.5">Services</p>
                                <Chips items={d.service} color="bg-yellow-500/10 border-yellow-500/20 text-yellow-700 dark:text-yellow-300" />
                            </div>
                            <div className="mt-3">
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-1.5">Schedule</p>
                                <Chips items={schedules} color="bg-purple-500/10 border-purple-500/20 text-purple-700 dark:text-purple-300" />
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                            <div className="flex items-center gap-1.5 mb-2 border-b border-slate-200 dark:border-gray-700 pb-1.5">
                                <Activity className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                                <h4 className="text-[10px] font-black text-cyan-600 dark:text-cyan-400 uppercase tracking-widest">Statistics</h4>
                            </div>
                            <div className="space-y-0.5">
                                {renderDetailRow("Hit Count", d.hit_count.toLocaleString())}
                                {renderDetailRow("Byte Count", fmtBytes(d.byte_count))}
                            </div>
                            <p className="text-[9px] text-slate-500 dark:text-gray-200 mt-3 leading-relaxed">{d.hit_count_description}</p>

                            {/* Identifiers */}
                            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-gray-700">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <Hash className="w-3 h-3 text-slate-500 dark:text-gray-200" />
                                    <span className="text-[9px] font-black text-slate-500 dark:text-gray-200 uppercase tracking-widest">Identifiers</span>
                                </div>
                                <div className="space-y-0.5">
                                    {renderDetailRow("Rule ID", null)}
                                    <p className="font-mono text-[9px] text-slate-500 dark:text-gray-400 break-all">{d.rule_id}</p>
                                </div>
                            </div>

                            {d.comments && (
                                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-gray-700">
                                    <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-1">Comments</p>
                                    <p className="text-[10px] text-slate-700 dark:text-gray-300">{d.comments}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallRules({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [rules, setRules] = useState<FirewallRuleItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<FirewallRuleItem | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [filter, setFilter] = useState<"enable" | "disable" | "all">("all");

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchRules = async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (filter !== "all") queryObj["status"] = filter;
            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { status: { $ilike: `%${debouncedSearch}%` } },
                ];
            }
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            const res = await baseapi.get("/firewall_rules", { params });
            if (res.data.status) {
                setRules(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load rules");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (!selected) fetchRules();
    }, [asset_id, debouncedSearch, currentPage, limit, filter, selected]);



    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    if (selected) return <RuleDetailView item={selected} onBack={() => setSelected(null)} />;

    const Th = ({ label, right = false }: { label: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-slate-600 dark:text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}

        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label}

            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-gray-800 dark:text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-red-600/10 dark:bg-red-600/20 p-1.5 rounded-lg border border-red-500/20 dark:border-red-500/30">
                        <Shield size={16} className="text-red-600 dark:text-red-400" />
                    </div>
                    <h1 className="text-lg font-bold text-gray-900 dark:text-white tracking-wide">Firewall Rules</h1>
                    <span className="text-[10px] font-bold text-slate-600 dark:text-gray-200 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 px-2 py-0.5 rounded shadow-sm">
                        {totalRecords} rules
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchRules}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-red-500"} />
                    </button>
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-200 group-focus-within:text-red-500 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search rules..."
                            className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-red-500/50 transition-all"
                        />
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setFilter("all")}
                            className={`px-2.5 py-1.5 border rounded-lg transition-all group shrink-0 text-[11px] font-bold ${filter === "all"
                                ? "bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400"
                                : "bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700"
                                }`}
                        >
                            All
                        </button>

                        <button
                            onClick={() => setFilter("enable")}
                            className={`px-2.5 py-1.5 border rounded-lg transition-all group shrink-0 text-[11px] font-bold ${filter === "enable"
                                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400"
                                : "bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700"
                                }`}
                        >
                            Enabled
                        </button>
                        <button
                            onClick={() => setFilter("disable")}
                            className={`px-2.5 py-1.5 border rounded-lg transition-all group shrink-0 text-[11px] font-bold ${filter === "disable"
                                ? "bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400"
                                : "bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-700"
                                }`}
                        >
                            Disabled
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <Th label="#" />
                                <Th label="Rule Name" />
                                <Th label="Status" />
                                <Th label="Action" />
                                <Th label="Source" />
                                <Th label="Destination" />
                                <Th label="Services" />
                                <Th label="Schedule" />
                                <Th label="NAT" />
                                <Th label="Hit Count" right />
                                <Th label="Bytes" right />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-gray-200">
                                            <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading rules...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : rules.length === 0 ? (
                                <tr>
                                    <td colSpan={11} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-60 dark:opacity-40">
                                            <Shield size={24} className="text-slate-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">No rules found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                rules.map((item) => {
                                    const d = parseData(item.data);
                                    const schedules = d ? formatScheduleArray(d.schedule) : [];

                                    return (
                                        <tr
                                            key={item.id}
                                            onClick={() => setSelected(item)}
                                            className="hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            title="Click to view details"
                                        >
                                            {/* Rule # */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="text-[10px] font-mono font-bold text-slate-600 dark:text-gray-200">
                                                    {d?.rule_number ?? item.id}
                                                </span>
                                            </td>

                                            {/* Name */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <div>
                                                        <p className="text-[11px] font-bold text-gray-900 dark:text-gray-100">{item.name.replace(/_/g, " ")}</p>
                                                        {d?.comments && <p className="text-[9px] text-slate-500 dark:text-gray-200 truncate max-w-[160px]">{d.comments}</p>}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <StatusBadge status={item.status} />
                                            </td>

                                            {/* Action */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {d ? <ActionBadge action={d.action} /> : "—"}
                                            </td>

                                            {/* Source */}
                                            <td className="px-4 py-2.5">
                                                {d ? (
                                                    <div className="flex flex-col gap-1">
                                                        <Chips items={d.source_zone} color="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-300" />
                                                        <Chips items={d.source_address.slice(0, 2)} color="bg-slate-100 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600/30 text-slate-700 dark:text-slate-300" />
                                                        {d.source_address.length > 2 && (
                                                            <span className="text-[8px] text-slate-500 dark:text-gray-200">+{d.source_address.length - 2} more</span>
                                                        )}
                                                    </div>
                                                ) : "—"}
                                            </td>

                                            {/* Destination */}
                                            <td className="px-4 py-2.5">
                                                {d ? (
                                                    <div className="flex flex-col gap-1">
                                                        <Chips items={d.destination_zone} color="bg-orange-500/10 border-orange-500/20 text-orange-600 dark:text-orange-300" />
                                                        <Chips items={d.destination_address.slice(0, 2)} color="bg-slate-100 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600/30 text-slate-700 dark:text-slate-300" />
                                                        {d.destination_address.length > 2 && (
                                                            <span className="text-[8px] text-slate-500 dark:text-gray-200">+{d.destination_address.length - 2} more</span>
                                                        )}
                                                    </div>
                                                ) : "—"}
                                            </td>

                                            {/* Services */}
                                            <td className="px-4 py-2.5">
                                                {d ? (
                                                    <div className="flex flex-col gap-1">
                                                        <Chips items={d.service.slice(0, 2)} color="bg-yellow-500/10 border-yellow-500/20 text-yellow-700 dark:text-yellow-300" />
                                                        {d.service.length > 2 && (
                                                            <span className="text-[8px] text-slate-500 dark:text-gray-200">+{d.service.length - 2} more</span>
                                                        )}
                                                    </div>
                                                ) : "—"}
                                            </td>

                                            {/* Schedule */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {schedules.length > 0 ? (
                                                    <Chips items={schedules} color="bg-purple-500/10 border-purple-500/20 text-purple-700 dark:text-purple-300" />
                                                ) : "—"}
                                            </td>

                                            {/* NAT */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {d ? (
                                                    <span className={`text-[10px] font-bold ${d.nat_enabled ? "text-emerald-600 dark:text-green-400" : "text-slate-400 dark:text-gray-200"}`}>
                                                        {d.nat_enabled ? <CheckCircle2 size={13} className="inline" /> : <XCircle size={13} className="inline" />}
                                                    </span>
                                                ) : "—"}
                                            </td>

                                            {/* Hit Count */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className={`text-[11px] font-mono font-bold ${d && d.hit_count > 0 ? "text-cyan-600 dark:text-cyan-300" : "text-slate-400 dark:text-gray-200"}`}>
                                                    {d ? fmtNumber(d.hit_count) : "—"}
                                                </span>
                                            </td>

                                            {/* Bytes */}
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <span className={`text-[11px] font-mono font-bold ${d && d.byte_count > 0 ? "text-slate-700 dark:text-gray-300" : "text-slate-400 dark:text-gray-200"}`}>
                                                    {d ? fmtBytes(d.byte_count) : "—"}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50/50 dark:bg-gray-800/40 border-t border-slate-200 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-slate-500 dark:text-gray-200 tracking-widest">
                            <span className="text-slate-800 dark:text-gray-300 font-bold">{startRecord}</span>
                            {" - "}
                            <span className="text-slate-800 dark:text-gray-300 font-bold">{endRecord}</span>
                            <span className="mx-1 text-slate-400 dark:text-gray-200">/</span>
                            <span className="text-slate-800 dark:text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 border border-slate-200 dark:border-transparent hover:bg-slate-100 dark:hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 border border-slate-200 dark:border-transparent hover:bg-slate-100 dark:hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 rounded text-[10px] font-bold mx-1 font-mono">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 border border-slate-200 dark:border-transparent hover:bg-slate-100 dark:hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 border border-slate-200 dark:border-transparent hover:bg-slate-100 dark:hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
